/**
 * Shared telemetry hook for frontend instrumentation.
 *
 * RUNTIME-003: Centralizes telemetry emission so all renderers and pages
 * go through a single hook instead of ad-hoc console calls or scattered
 * analytics. Uses a lightweight event buffer that can be flushed to a
 * backend endpoint or observability pipeline.
 */
import { useCallback, useRef } from 'react';
import type { TelemetryId } from '@finance/contracts';
import { browserLogger } from '../logging/browser-logger';

export interface TelemetryEvent {
  telemetryId: TelemetryId;
  timestamp: number;
  [key: string]: unknown;
}

type TelemetrySink = (event: TelemetryEvent) => void;

let sink: TelemetrySink = (event) => {
  browserLogger.debug('telemetry.event', 'Telemetry event emitted', event);
};

export function setTelemetrySink(next: TelemetrySink): void {
  sink = next;
}

export function useTelemetry() {
  const bufferRef = useRef<TelemetryEvent[]>([]);

  const track = useCallback((telemetryId: TelemetryId, payload?: Record<string, unknown>) => {
    const event: TelemetryEvent = {
      telemetryId,
      timestamp: Date.now(),
      ...payload,
    };
    bufferRef.current.push(event);
    sink(event);
  }, []);

  const flush = useCallback(() => {
    const events = bufferRef.current;
    bufferRef.current = [];
    return events;
  }, []);

  return { track, flush };
}
